/**
 * Intern prosjektrapport — ren kalkyle (VIB-1068).
 *
 * READ-ONLY: rapporten AVLEDER tall fra de kanoniske kildene
 * (projects.bf_estimated/bf_actual, budget_lines, budget_line_assignments,
 * voucher_allocations, invoices) og konkurrerer aldri med KPI-motorene
 * (docs/kpi-source-of-truth.md). Intern timekost er UTE av scope
 * (Erik 07.07) — kostnadsdelen er kun eksterne bilag; timer vises som timer.
 *
 * Ekstern-kost-kalkylen her (estimert `estimated_cost` vs. påløpte
 * godkjente/`forwarded_to_dl`-allokeringer per linje) gjenbrukes av VIB-1295
 * (Tilbud-fanens «Påløpt kostnad»-kolonne) i bølge 4.
 *
 * No Supabase/React imports.
 */

import { isTaskLine } from "./billing-calculations";
import { resolveBillingAttributionDate } from "./billing-attribution";
import { BF_COST_VOUCHER_STATUSES, INVOICED_STATUSES as KPI_INVOICED_STATUSES } from "./kpi-calculations";

// ── Input shapes (delmengder av use-project-detail-typene) ─────────────

export interface ReportBudgetLine {
  id: string;
  phase: string | null;
  description: string | null;
  cost_type: string | null;
  quantity_hours: number | null;
  amount: number | null;
  estimated_cost: number | null;
  assignments?: {
    user_id: string | null;
    hours: number | null;
    task_status: string | null;
    completed_at?: string | null;
    approved_at?: string | null;
  }[];
}

export interface ReportAllocation {
  budget_line_id: string | null;
  amount: number | null;
  voucher_status: string | null;
}

export interface BaselineLine {
  budget_line_id: string;
  phase: string | null;
  description: string | null;
  cost_type: string | null;
  quantity_hours: number | null;
  amount: number | null;
  estimated_cost: number | null;
  captured_at: string;
}

export interface ReportInvoice {
  amount: number;
  amount_excl_vat: number | null;
  status: string | null;
}

// ── Ekstern kost (VIB-1295-gjenbruk) ───────────────────────────────────

/** Bilag-statuser som teller som påløpt kost — SAMME konstant som BF actual (kpi-calculations). */
const ACCRUED_VOUCHER_STATUSES: ReadonlySet<string> = new Set(BF_COST_VOUCHER_STATUSES);

/** budget_line_id → påløpt ekstern kost (godkjente/forwarded_to_dl-allokeringer). */
export function accruedExternalByLine(allocations: ReportAllocation[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const a of allocations) {
    if (!a.budget_line_id) continue;
    if (!ACCRUED_VOUCHER_STATUSES.has(a.voucher_status ?? "")) continue;
    map.set(a.budget_line_id, (map.get(a.budget_line_id) ?? 0) + Number(a.amount ?? 0));
  }
  return map;
}

export interface ExternalCostRow {
  lineId: string;
  description: string;
  phase: string | null;
  estimatedKr: number;
  accruedKr: number;
  deviationKr: number;
  /** null når estimatet er 0 (ingen basis for prosent). */
  deviationPct: number | null;
}

export interface ExternalCostSummary {
  rows: ExternalCostRow[];
  totalEstimatedKr: number;
  totalAccruedKr: number;
}

/**
 * Estimert vs. påløpt ekstern kost per ekstern budsjettlinje. Linjer uten
 * estimat OG uten påløpt utelates (støy).
 */
export function calcExternalCosts(
  lines: ReportBudgetLine[],
  accruedByLine: Map<string, number>,
): ExternalCostSummary {
  const rows: ExternalCostRow[] = [];
  for (const l of lines) {
    if (l.cost_type !== "external") continue;
    const estimated = Number(l.estimated_cost ?? 0);
    const accrued = accruedByLine.get(l.id) ?? 0;
    if (estimated === 0 && accrued === 0) continue;
    const deviationKr = accrued - estimated;
    rows.push({
      lineId: l.id,
      description: l.description ?? "—",
      phase: l.phase,
      estimatedKr: estimated,
      accruedKr: accrued,
      deviationKr,
      deviationPct: estimated !== 0 ? Math.round((deviationKr / estimated) * 1000) / 10 : null,
    });
  }
  rows.sort((a, b) => Math.abs(b.deviationKr) - Math.abs(a.deviationKr));
  return {
    rows,
    totalEstimatedKr: rows.reduce((s, r) => s + r.estimatedKr, 0),
    totalAccruedKr: rows.reduce((s, r) => s + r.accruedKr, 0),
  };
}

// ── Timer per fase og per person ───────────────────────────────────────

export interface PhaseHoursRow {
  phase: string;
  budgetedHours: number;
  loggedHours: number;
  deviationHours: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Timer brukt vs. budsjettert per fase (interne linjer; timer, ikke kroner). */
export function calcPhaseHours(
  lines: ReportBudgetLine[],
  loggedByLine: Map<string, number>,
): PhaseHoursRow[] {
  const byPhase = new Map<string, { budgeted: number; logged: number }>();
  for (const l of lines) {
    if (l.cost_type === "external") continue;
    const phase = l.phase ?? "ukjent";
    const cur = byPhase.get(phase) ?? { budgeted: 0, logged: 0 };
    cur.budgeted += Number(l.quantity_hours ?? 0);
    cur.logged += loggedByLine.get(l.id) ?? 0;
    byPhase.set(phase, cur);
  }
  return [...byPhase.entries()].map(([phase, v]) => ({
    phase,
    budgetedHours: round1(v.budgeted),
    loggedHours: round1(v.logged),
    deviationHours: round1(v.logged - v.budgeted),
  }));
}

export interface PersonHoursRow {
  userId: string;
  assignedHours: number;
  loggedHours: number;
}

/** Timer tildelt vs. logget per person (fra assignments + logget-per-bruker). */
export function calcPersonHours(
  lines: ReportBudgetLine[],
  loggedPerUser: Map<string, Map<string, number>>,
): PersonHoursRow[] {
  const byUser = new Map<string, { assigned: number; logged: number }>();
  for (const l of lines) {
    for (const a of l.assignments ?? []) {
      if (!a.user_id) continue;
      const cur = byUser.get(a.user_id) ?? { assigned: 0, logged: 0 };
      cur.assigned += Number(a.hours ?? 0);
      byUser.set(a.user_id, cur);
    }
    const lu = loggedPerUser.get(l.id);
    if (lu) {
      for (const [userId, hours] of lu) {
        const cur = byUser.get(userId) ?? { assigned: 0, logged: 0 };
        cur.logged += hours;
        byUser.set(userId, cur);
      }
    }
  }
  return [...byUser.entries()]
    .map(([userId, v]) => ({
      userId,
      assignedHours: round1(v.assigned),
      loggedHours: round1(v.logged),
    }))
    .sort((a, b) => b.loggedHours - a.loggedHours);
}

// ── Faktureringsgrad-bidrag (samme definisjon som use-billing-rates) ────

/**
 * Sum tildelte timer som teller etter den kanoniske godkjenningsregelen —
 * prosjektets bidrag til faktureringsgraden. INGEN ny variant av formelen:
 * samme isTaskLine-gate (cost_type='internal' AND quantity_hours>0) som
 * accumulateBillableByUserMonth i billing-rates-calc — reisetid-linjer
 * (internal, qty 0) krediteres der fra loggede timer, ikke assignments.
 */
export function calcBillingContributionHours(
  lines: ReportBudgetLine[],
  asOf: Date = new Date(),
): number {
  let sum = 0;
  for (const l of lines) {
    if (!isTaskLine(l)) continue;
    for (const a of l.assignments ?? []) {
      if (resolveBillingAttributionDate(a, null, asOf)) {
        sum += Number(a.hours ?? 0);
      }
    }
  }
  return round1(sum);
}

// ── Fakturert vs. gjenstående ──────────────────────────────────────────

/** Statuser som teller som fakturert — SAMME konstant som omsetnings-KPI-en (kpi-calculations). */
const INVOICED_STATUSES: ReadonlySet<string> = new Set(KPI_INVOICED_STATUSES);

export interface InvoicedSummary {
  invoicedKr: number;
  contractKr: number;
  remainingKr: number;
}

export function calcInvoiced(
  invoices: ReportInvoice[],
  totalAmount: number | null,
): InvoicedSummary {
  const invoicedKr = invoices.reduce(
    (s, inv) =>
      INVOICED_STATUSES.has(inv.status ?? "")
        ? s + Number(inv.amount_excl_vat ?? inv.amount ?? 0)
        : s,
    0,
  );
  const contractKr = Number(totalAmount ?? 0);
  return { invoicedKr, contractKr, remainingKr: contractKr - invoicedKr };
}

// ── Debrief: baseline vs. nå ───────────────────────────────────────────

/**
 * Baseline-fangsten (VIB-1057 #2) ble deployet 24.06.2026; eldre rader er
 * backfillet med captured_at = linjens created_at og kan avvike fra det
 * OPPRINNELIGE estimatet hvis linjen ble redigert før fangsten fantes.
 */
export const BASELINE_CAPTURE_DEPLOYED = "2026-06-25";

export interface DebriefRow {
  budgetLineId: string;
  description: string;
  phase: string | null;
  baselineHours: number;
  currentHours: number;
  baselineAmountKr: number;
  currentAmountKr: number;
  deviationHours: number;
  deviationAmountKr: number;
  /** null når baseline-timene er 0. */
  deviationHoursPct: number | null;
}

export interface DebriefSummary {
  rows: DebriefRow[];
  /** Linjer som finnes i baseline men er slettet fra budsjettet. */
  removed: BaselineLine[];
  /** Nåværende linjer uten baseline — lagt til etter oppstart. */
  added: ReportBudgetLine[];
  /** True når noen baseline-rader er backfillet (upresis debrief-basis). */
  hasBackfilledBaseline: boolean;
}

export function calcDebrief(
  lines: ReportBudgetLine[],
  baseline: BaselineLine[],
): DebriefSummary {
  const baselineById = new Map(baseline.map((b) => [b.budget_line_id, b]));
  const lineIds = new Set(lines.map((l) => l.id));

  const rows: DebriefRow[] = [];
  const added: ReportBudgetLine[] = [];
  for (const l of lines) {
    const b = baselineById.get(l.id);
    if (!b) {
      added.push(l);
      continue;
    }
    const baselineHours = Number(b.quantity_hours ?? 0);
    const currentHours = Number(l.quantity_hours ?? 0);
    const baselineAmountKr = Number(b.amount ?? 0);
    const currentAmountKr = Number(l.amount ?? 0);
    rows.push({
      budgetLineId: l.id,
      description: l.description ?? b.description ?? "—",
      phase: l.phase,
      baselineHours,
      currentHours,
      baselineAmountKr,
      currentAmountKr,
      deviationHours: round1(currentHours - baselineHours),
      deviationAmountKr: currentAmountKr - baselineAmountKr,
      deviationHoursPct:
        baselineHours !== 0
          ? Math.round(((currentHours - baselineHours) / baselineHours) * 1000) / 10
          : null,
    });
  }
  rows.sort(
    (a, b) => Math.abs(b.deviationAmountKr) - Math.abs(a.deviationAmountKr),
  );

  const removed = baseline.filter((b) => !lineIds.has(b.budget_line_id));
  const hasBackfilledBaseline = baseline.some(
    (b) => b.captured_at.slice(0, 10) < BASELINE_CAPTURE_DEPLOYED,
  );

  return { rows, removed, added, hasBackfilledBaseline };
}
